import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home as HomeIcon } from 'lucide-react';

/**
 * Universal back arrow + home icon. Uses browser history when available,
 * otherwise falls back to a role-appropriate home. Home icon always goes to '/'.
 * Hidden on the fallback destination itself.
 */
export default function BackButton({ fallback, label = 'Back', className = '', size = 'sm', showHome = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBack = window.history.length > 1;

  const target = fallback || '/';
  const onHome = location.pathname === '/';
  if (onHome && !canGoBack) return null;

  const handleBack = () => {
    if (canGoBack) navigate(-1);
    else navigate(target);
  };

  const handleHome = () => navigate('/');

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {!onHome && (
        <Button
          data-testid="back-arrow-btn"
          onClick={handleBack}
          variant="ghost"
          size={size}
          className="rounded-full hover:bg-muted"
          aria-label={label}
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          <span className="hidden sm:inline">{label}</span>
        </Button>
      )}
      {showHome && !onHome && (
        <Button
          data-testid="home-btn"
          onClick={handleHome}
          variant="ghost"
          size={size}
          className="rounded-full hover:bg-muted"
          aria-label="Home"
        >
          <HomeIcon className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
